import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import { ChevronDown, CircleAlert, Clock3, HeartPulse, Radio, Wallet as WalletIcon } from "lucide-react";
import { useFacility } from "../../context/FacilityContext";
import { VoiceButton, VoiceStrip, useVoiceInput } from "../components/VoiceInput";
import { OlliSendButton } from "../components/assistant/OlliSendButton";
import { logPrompt } from "../components/assistant/RecentPrompts";
import { ConversationMark } from "../components/OlliLogo";
import { PageLoader } from "../components/PageLoader";
import { useOutside } from "../components/crm/ui";
import { statusOf } from "../lib/ads";
import { CURRENCIES, RANGE_OPTIONS, setDisplayCurrency } from "../lib/home";
import { openOlli } from "../lib/olli";
import { useAskOlli } from "../../hooks/useFeatureFlags";
import { TAB_GROUPS, useHomeBase, useHomeRange, useLiveStats } from "../components/home/useHomeData";
import OverviewTab from "../components/home/OverviewTab";
import AppointmentsTab from "../components/home/AppointmentsTab";
import PatientsRevenueTab from "../components/home/PatientsRevenueTab";
import GrowthTab from "../components/home/GrowthTab";
import ConversationsTab from "../components/home/ConversationsTab";
import AiOperationsTab from "../components/home/AiOperationsTab";
import { HeroSlides } from "../components/home/HeroSlides";

/**
 * Home / Dashboard (design: screens/CommandCenter.tsx and components/home/*).
 *
 * Layout, wording and styling are the design's. Real data and actions:
 *   clinic name, user name ................. /api/organizations/my-organizations, signed-in user
 *   counts, wallet, live activity feed ..... /api/dashboard/stats (refreshed every minute)
 *   appointments (range, today, upcoming) .. /api/appointments?date_from&date_to
 *   doctor shifts, utilisation ............. /api/doctor-schedules
 *   new / returning patients ............... /api/patients?date_from&date_to, appointments
 *   leads, by source, by day, funnel ....... /api/lead-analytics/pivot, /api/lead-statuses
 *   converted leads ........................ /api/lead-analytics/pivot (measure=won)
 *   treatment plans started, by treatment .. /api/patient-treatments?date_from&date_to
 *   overdue follow-ups ..................... /api/leads/tasks/all
 *   open complaints, by priority ........... /api/complaints?status=open,acknowledged,in_progress,waiting_on_patient,reopened
 *   WhatsApp conversations, unread, waiting  /api/waba/conversations, /api/dashboard/stats
 *   Instagram / Messenger threads .......... /api/social-inbox, /api/social-inbox/stats
 *   AI agents, their conversations ......... /api/ai-agent/config, /api/ai-agent/conversations
 *   knowledge base documents ............... /api/kb/documents (clinic owners only)
 *   Olli credits and recent activity ....... /api/dashboard/stats wallet, /api/waba/wallet/transactions
 *   campaigns, in review, ad spend ......... /api/ads/campaigns, /api/ads/insights/:id
 *   Facebook / Instagram audience .......... /api/insights/overview, /api/instagram/account,
 *                                            /api/instagram/insights/account
 *   Ask Olli box → the floating assistant ... /api/olli (item 130)
 *   collections, by day and by treatment ... /api/collections (131)
 *   treatment plan acceptance and value .... /api/patient-treatments/stats (132)
 *   new patients by source ................. /api/patients/stats (133)
 *   lead → appointment, visits ............. /api/lead-analytics/pivot booked, visited (134)
 *   WhatsApp first-response time ........... /api/waba/conversations/response-times (135)
 *   AI resolved on Instagram / Facebook .... /api/social-inbox/stats (136)
 *   No-shows → Send reminder ............... POST /api/appointments/:id/reminder (137)
 *   currency switcher ...................... /api/fx?base=INR (138); credits stay in USD
 *   Instagram interactions ................. /api/insights/performance (139)
 *   announcements (old dashboard slider) ... /api/dashboard-slides, managed in the admin panel
 * Meta features still in review (meta_ad_insights, meta_page_performance,
 * meta_instagram_insights) are read from /api/users/feature-flags first; when
 * off, their calls are skipped and the Growth cards say "coming soon".
 * Not possible: Google Ads spend (140) needs the Google Ads API, which Olli
 * isn't connected to.
 */

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "appointments", label: "Appointments" },
  { id: "patients-revenue", label: "Patients & revenue" },
  { id: "growth", label: "Growth" },
  { id: "conversations", label: "Conversations" },
  { id: "ai-operations", label: "AI & operations" },
];
const num = (value) => value.toLocaleString("en-IN");
const IST = { timeZone: "Asia/Kolkata" };

function useClinicClock() {
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return clock;
}

const greeting = (ms) => {
  const hour = Number(new Date(ms).toLocaleString("en-GB", { ...IST, hour: "numeric", hour12: false }));
  return hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
};

export default function Home() {
  const askOlliOn = useAskOlli();
  const fac = useFacility();
  const facilityId = fac.apiParams().facility_id || null;
  const facilityLabel = fac.currentFacility ? fac.currentFacility.name : "All locations";
  const clock = useClinicClock();
  const now = useMemo(() => Date.now(), []);
  const [query, setQuery] = useState("");
  const [orbState, setOrbState] = useState("idle");
  const [range, setRange] = useState("30d");
  const [currency, setCurrency] = useState("INR");
  // Amounts are stored in INR; the switcher converts what is shown (item 138).
  const [fxRates, setFxRates] = useState(null);
  useEffect(() => {
    axios.get(`${config.API_BASE_URL}/api/fx?base=INR`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then((r) => setFxRates(r.data?.rates || null)).catch(() => setFxRates(null));
  }, []);
  // Set before the tabs render, so every money() below uses it; without a rate it stays in ₹.
  setDisplayCurrency(currency, fxRates?.[currency]);
  const [tab, setTab] = useState("overview");
  // Each tab's extra calls start the first time it is opened, and keep refreshing after.
  const [groups, setGroups] = useState({});
  const openTab = (id) => {
    setTab(id);
    setGroups((prev) => (TAB_GROUPS[id].every((g) => prev[g]) ? prev : { ...prev, ...Object.fromEntries(TAB_GROUPS[id].map((g) => [g, true])) }));
  };

  const live = useLiveStats(facilityId);
  const base = useHomeBase(facilityId, groups);
  const period = useHomeRange(range, facilityId, now, base, groups);
  const tabReady = TAB_GROUPS[tab].every((g) => base.ready[g] !== false && period.ready[g] !== false);
  const data = { base, period, stats: live.stats, facilityId, scoped: !!facilityId, now };

  const wallet = live.stats?.wallet;
  const overdue = base.overdueTotal || 0;
  const openComplaints = base.complaints?.length || 0;
  const adsInReview = (base.campaigns || []).filter((c) => statusOf(c) === "In review").length;
  const failedDocs = (base.kbDocs || []).filter((doc) => doc.status === "failed").length;
  const attention = [
    ...(overdue ? [{ label: `${num(overdue)} overdue follow-up${overdue === 1 ? "" : "s"}`, to: "/crm/tasks", icon: Clock3 }] : []),
    ...(openComplaints ? [{ label: `${num(openComplaints)} open patient concern${openComplaints === 1 ? "" : "s"}`, to: "/support/complaints", icon: HeartPulse }] : []),
    ...(adsInReview ? [{ label: `${num(adsInReview)} campaign${adsInReview === 1 ? "" : "s"} in review`, to: "/ads/campaigns", icon: Radio }] : []),
    ...(failedDocs ? [{ label: `${num(failedDocs)} knowledge document${failedDocs === 1 ? "" : "s"} failed`, to: "/knowledge-base", icon: CircleAlert }] : []),
    ...(wallet && wallet.balance < wallet.low_balance_threshold ? [{ label: "Olli credits are running low", to: "/wallet", icon: WalletIcon }] : []),
  ];
  const clinicStatus = attention.length ? `${attention.length} ${attention.length === 1 ? "item needs" : "items need"} your attention` : "All systems normal";
  const formattedDate = new Date(clock).toLocaleDateString("en-IN", { ...IST, weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const time = new Date(clock).toLocaleTimeString("en-IN", { ...IST, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
  const syncedAt = live.loadedAt ? new Date(live.loadedAt).toLocaleTimeString("en-IN", { ...IST, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }) : null;

  const voice = useVoiceInput({ onTranscript: (spoken) => setQuery(spoken) });
  useEffect(() => { if (voice.status === "idle" || voice.status === "finishing") setOrbState("idle"); }, [voice.status]);
  const submitPrompt = (event) => {
    event.preventDefault();
    const prompt = query.trim();
    if (!prompt) return;
    logPrompt("home", prompt);
    // The assistant panel opens with the prompt and answers there (item 130);
    // it records the chat on this prompt so "Open chat" can find it.
    openOlli(prompt, { surface: "home" });
    setQuery("");
  };

  const askContent = <form id="olli-command-form" onSubmit={submitPrompt} className="flex w-full min-w-0 items-center gap-2 rounded-full bg-paper py-1.5 pl-2 pr-1.5 shadow-soft ring-1 ring-line focus-within:ring-sky-deep/50">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink"><ConversationMark tone="black" className="h-6 w-6" /></span>
    <label htmlFor="olli-command" className="sr-only">Ask Olli</label>
    <input id="olli-command" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Ask Olli to book, fill a form or launch anything" className="h-11 min-w-0 flex-1 bg-transparent px-2 text-body text-ink outline-none placeholder:text-muted" />
    {voice.status !== "idle" && <span className="hidden sm:block"><VoiceStrip status={voice.status} levels={voice.levels} seconds={voice.seconds} notice={voice.notice} onStop={() => { voice.stop(); setOrbState("idle"); }} /></span>}
    <VoiceButton status={voice.status} onStart={() => { setOrbState("listening"); void voice.start(); }} onStop={() => { voice.stop(); setOrbState("idle"); }} />
    <OlliSendButton label="Ask Olli" sendingLabel="Sending…" sending={false} disabled={!query.trim()} />
  </form>;

  // First load: Olli's loader until the core calls answer, instead of a page of "—".
  // Later range or location changes keep the current numbers on screen while they refresh.
  const firstLoad = !base.loaded || !period.loaded || (!live.stats && !live.failed);
  if (firstLoad) return <main className="mx-auto w-full max-w-7xl min-w-0 px-4 py-5 text-ink sm:px-8 sm:py-8"><PageLoader label="Loading your dashboard…" className="min-h-[80vh]" /></main>;

  return <main className="mx-auto w-full max-w-7xl min-w-0 px-4 py-5 text-ink sm:px-8 sm:py-8"><section className="flex min-w-0 flex-col gap-5" aria-label="Olli command center">
    <header className="flex min-w-0 flex-wrap items-start justify-between gap-4 border-b border-line pb-4">
      <div className="min-w-0"><p className="text-small text-muted">{base.clinicName ? `${base.clinicName} · ` : ""}{facilityLabel}</p><h1 className="mt-1 text-h1 font-semibold text-ink">{greeting(clock)}{base.userName ? `, ${base.userName}` : ""}</h1><p className="mt-1 text-small text-muted">Your clinic, connected in one place.</p></div>
      <div className="flex min-w-0 flex-col items-start gap-3 sm:items-end">
        <div className="flex flex-wrap items-center gap-3 sm:justify-end">
          <label className="relative inline-flex h-12 items-center rounded-full bg-mist pl-4 pr-9 text-small font-semibold text-ink focus-within:ring-2 focus-within:ring-sky-deep/40"><span className="sr-only">Currency</span><select value={currency} onChange={(event) => setCurrency(event.target.value)} aria-label="Dashboard currency" className="cursor-pointer appearance-none bg-transparent pr-1 outline-none">{CURRENCIES.map((item) => <option key={item.code} value={item.code} disabled={item.code !== "INR" && !(Number(fxRates?.[item.code]) > 0)}>{item.symbol === item.code ? item.code : `${item.symbol} ${item.code}`}</option>)}</select><ChevronDown size={16} strokeWidth={1.75} className="pointer-events-none absolute right-3 text-muted" /></label>
          <div className="flex w-fit flex-wrap rounded-full bg-mist p-1" role="group" aria-label="Dashboard date range">{RANGE_OPTIONS.map((item) => <button key={item.id} type="button" aria-pressed={range === item.id} onClick={() => setRange(item.id)} className={`h-10 rounded-full px-4 text-small font-semibold transition-colors ${range === item.id ? "bg-paper text-ink shadow-soft" : "text-muted hover:text-ink"}`}>{item.label}</button>)}</div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-muted"><AttentionPill attention={attention} label={clinicStatus} />{currency !== "INR" && <span className="text-caption">Amounts stay in ₹ · no exchange rates yet</span>}<span className="font-mono text-caption tabular-nums">{formattedDate}</span><span className="font-mono text-small tabular-nums text-ink">{time} <span className="text-muted">IST</span></span></div>
      </div>
    </header>
    <HeroSlides />
    {askOlliOn && askContent}
    <nav role="tablist" aria-label="Dashboard sections" className="flex min-w-0 gap-1 overflow-x-auto border-b border-line">{TABS.map((item) => <button key={item.id} type="button" role="tab" aria-selected={tab === item.id} onClick={() => openTab(item.id)} className={`shrink-0 border-b-2 px-3 py-3 text-small font-semibold transition-colors sm:px-4 ${tab === item.id ? "border-ink text-ink" : "border-transparent text-muted hover:text-ink"}`}>{item.label}</button>)}</nav>
    {!tabReady && <PageLoader label={`Loading ${TABS.find((item) => item.id === tab).label.toLowerCase()}…`} className="min-h-[50vh]" />}
    {tabReady && tab === "overview" && <OverviewTab range={range} orbState={orbState} attention={attention} data={data} facilityLabel={facilityLabel} healthy={!live.failed && base.loaded} />}
    {tabReady && tab === "appointments" && <AppointmentsTab range={range} data={data} />}
    {tabReady && tab === "patients-revenue" && <PatientsRevenueTab range={range} data={data} />}
    {tabReady && tab === "growth" && <GrowthTab range={range} data={data} />}
    {tabReady && tab === "conversations" && <ConversationsTab range={range} data={data} />}
    {tabReady && tab === "ai-operations" && <AiOperationsTab range={range} data={data} />}
    <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3 text-caption text-muted"><span>Platform overview · {formattedDate}</span><span className="font-mono tabular-nums">{syncedAt ? `Core sync ${syncedAt} IST` : "Core sync pending"}</span></footer>
  </section></main>;
}

/**
 * The status pill under the greeting. With one item it opens that item's page;
 * with several it lists them, each linking to where it is dealt with. With
 * nothing to do it is a plain "All systems normal".
 */
function AttentionPill({ attention, label }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useOutside(() => setOpen(false), open);
  const base = "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-caption";
  if (!attention.length) {
    return <span className={`${base} bg-success-soft text-success`}><span className="h-2 w-2 rounded-full bg-success" />{label}</span>;
  }
  const onClick = () => (attention.length === 1 ? navigate(attention[0].to) : setOpen((v) => !v));
  return (
    <span ref={ref} className="relative">
      <button type="button" onClick={onClick} aria-expanded={attention.length > 1 ? open : undefined} className={`${base} bg-warning-soft font-medium text-warning hover:brightness-95`}>
        <span className="h-2 w-2 rounded-full bg-warning" />
        {label}
        {attention.length > 1 ? <ChevronDown size={14} strokeWidth={2} className={open ? "rotate-180" : ""} /> : <span aria-hidden>→</span>}
      </button>
      {open && (
        <ul className="absolute left-0 top-full z-40 mt-2 w-72 animate-nav-in rounded-lg bg-paper p-2 shadow-lift ring-1 ring-line">
          {attention.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.label}>
                <Link to={item.to} onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-small text-ink hover:bg-mist">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sun-soft"><Icon size={14} strokeWidth={1.75} /></span>
                  <span className="min-w-0 flex-1">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </span>
  );
}
